import { useMemo, useState } from "react";
import { PageHeader, CTASection, Disclaimer } from "@/components/Shared";
import {
  Calculator, GraduationCap, Users, BookOpen, Plane, ArrowRight, Info,
  HeartHandshake, ShieldCheck, FileSearch, Wallet, Printer, Clock,
  Home, Bus, Stethoscope, ChevronDown, AlertCircle, CheckCircle2, Coins,
} from "lucide-react";
import { Link } from "react-router-dom";

// ─────────────────────────────────────────────────────────────────────────────
// Department of Home Affairs — Student Visa (Subclass 500) financial capacity
// figures (effective 10 May 2024). Source: immi.homeaffairs.gov.au
// All values are AUD per 12 months unless otherwise noted.
// ─────────────────────────────────────────────────────────────────────────────
const LIVING_STUDENT       = 29710;   // Primary applicant — 12-month living costs
const LIVING_PARTNER       = 10394;   // Spouse / de facto partner
const LIVING_CHILD         = 4449;    // Each dependent child
const SCHOOLING_PER_CHILD  = 13502;   // Annual school costs per school-aged child
const INCOME_SINGLE        = 87856;   // Annual income — applicant only
const INCOME_WITH_FAMILY   = 102500;  // Annual income — with family member(s)

// Indicative defaults (not Home Affairs requirements — user-editable)
const OSHC_SINGLE_DEFAULT  = 750;
const OSHC_COUPLE_DEFAULT  = 3800;
const OSHC_FAMILY_DEFAULT  = 5500;
const TRAVEL_OFFSHORE      = 2000;
const TRAVEL_ONSHORE       = 0;

const fmtAUD = (n) =>
  new Intl.NumberFormat("en-AU", { style: "currency", currency: "AUD", maximumFractionDigits: 0 }).format(n);

function Tooltip({ children }) {
  return (
    <span className="group relative inline-flex items-center">
      <Info className="h-3.5 w-3.5 text-[#29324A] cursor-help" />
      <span className="invisible group-hover:visible opacity-0 group-hover:opacity-100 transition-opacity absolute left-1/2 -translate-x-1/2 bottom-full mb-2 w-60 bg-[#29324A] text-white text-[11px] leading-relaxed px-3 py-2 z-30 shadow-xl">
        {children}
      </span>
    </span>
  );
}

function NumberInput({ id, label, value, onChange, prefix = "AUD", min = 0, step = 100, testid, tooltip }) {
  return (
    <label htmlFor={id} className="block">
      <span className="text-[11px] font-bold tracking-[0.18em] text-[#29324A] uppercase mb-2 flex items-center gap-1.5">
        {label}
        {tooltip && <Tooltip>{tooltip}</Tooltip>}
      </span>
      <div className="flex items-stretch border border-slate-200 focus-within:border-[#29324A] bg-white">
        <span className="px-3 flex items-center text-xs font-bold tracking-wider uppercase text-slate-400 bg-slate-50 border-r border-slate-200">
          {prefix}
        </span>
        <input
          id={id}
          type="number"
          inputMode="numeric"
          min={min}
          step={step}
          value={value}
          onChange={(e) => onChange(Number(e.target.value) || 0)}
          data-testid={testid}
          className="flex-1 px-3 py-3 text-sm focus:outline-none w-full"
        />
      </div>
    </label>
  );
}

function Row({ label, value, dim }) {
  return (
    <div className={`flex items-baseline justify-between border-b border-white/10 py-1.5 ${dim ? "opacity-60" : ""}`}>
      <span className="text-slate-300 text-[13px]">{label}</span>
      <span className="font-display text-[15px] text-white">{fmtAUD(value)}</span>
    </div>
  );
}

function StepLegend({ icon: Icon, n, children }) {
  return (
    <legend className="text-[10px] font-bold tracking-[0.22em] text-[#C43A89] uppercase mb-4 flex items-center gap-2">
      <span className="h-5 w-5 rounded-full bg-[#C43A89] text-white text-[10px] flex items-center justify-center font-bold">{n}</span>
      <Icon className="h-3.5 w-3.5" />
      {children}
    </legend>
  );
}

export default function StudentFundsCalculator() {
  // ──── Inputs
  const [mode, setMode] = useState("funds"); // "funds" | "income"
  const [partner, setPartner] = useState(false);
  const [children, setChildren] = useState(0);
  const [schoolChildren, setSchoolChildren] = useState(0);

  const [courseMonths, setCourseMonths] = useState(12);
  const [tuition, setTuition] = useState(30000); // total tuition for the period chosen
  const [tuitionPrepaid, setTuitionPrepaid] = useState(0);
  const [oshc, setOshc] = useState(OSHC_SINGLE_DEFAULT);
  const [offshore, setOffshore] = useState(true);

  // Annual income alt mode
  const [annualIncome, setAnnualIncome] = useState(80000);

  // ──── Computed funds (Option A — 12-month funds, pro-rated if course < 12 months)
  const breakdown = useMemo(() => {
    const months   = Math.min(12, Math.max(1, Number(courseMonths) || 12));
    const factor   = months / 12;

    const livingStudent   = LIVING_STUDENT * factor;
    const livingPartner   = (partner ? LIVING_PARTNER : 0) * factor;
    const livingChildren  = Math.max(0, children) * LIVING_CHILD * factor;
    const schooling       = Math.max(0, Math.min(schoolChildren, children)) * SCHOOLING_PER_CHILD * factor;
    const tuitionNet      = Math.max(0, tuition - tuitionPrepaid);
    const oshcCost        = Math.max(0, oshc);
    const travel          = offshore ? TRAVEL_OFFSHORE : TRAVEL_ONSHORE;
    const total = livingStudent + livingPartner + livingChildren + schooling + tuitionNet + oshcCost + travel;

    return { months, factor, livingStudent, livingPartner, livingChildren, schooling, tuitionNet, oshcCost, travel, total };
  }, [partner, children, schoolChildren, tuition, tuitionPrepaid, oshc, offshore, courseMonths]);

  // ──── Annual income threshold (alternative)
  const incomeThreshold = useMemo(() => {
    const hasFamily = partner || children > 0;
    return {
      threshold: hasFamily ? INCOME_WITH_FAMILY : INCOME_SINGLE,
      hasFamily,
      meets: annualIncome >= (hasFamily ? INCOME_WITH_FAMILY : INCOME_SINGLE),
    };
  }, [annualIncome, partner, children]);

  const handlePrint = () => window.print();

  return (
    <div>
      <PageHeader
        eyebrow="Subclass 500 · Genuine Student"
        title="Australian Student Fund Calculator."
        subtitle="Estimate the funds you need to demonstrate financial capacity for an Australian Student Visa (Subclass 500). Toggle between the 12-month funds option and the annual income option."
        image="/assets/graduate.jpg"
      />

      {/* Intro */}
      <section className="py-12 md:py-14 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-slate-700 leading-relaxed text-[15px] space-y-4">
          <p>
            From <strong>10 May 2024</strong>, the Department of Home Affairs increased the financial capacity requirements for the Student Visa (Subclass 500). You must demonstrate sufficient genuine funds covering course fees, OSHC, travel and <strong>12 months</strong> of living and schooling costs for yourself and any accompanying family members.
          </p>
          <p>
            Use the calculator below to estimate your total funds. This is general guidance only — the Department makes the final determination of financial capacity based on the documents you submit. All figures should be cross-checked against{" "}
            <a href="https://immi.homeaffairs.gov.au" target="_blank" rel="noopener noreferrer" className="text-[#29324A] underline font-semibold">immi.homeaffairs.gov.au</a>.
          </p>
        </div>
      </section>

      {/* Mode toggle */}
      <section className="bg-[#F8F4FB]/60 py-6 border-y border-slate-200">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
          <div className="text-[11px] font-bold tracking-[0.22em] text-[#29324A] uppercase">
            Choose how you'll evidence financial capacity
          </div>
          <div className="inline-flex border border-[#29324A] p-0.5 self-start sm:self-auto">
            <button
              type="button"
              onClick={() => setMode("funds")}
              data-testid="mode-funds"
              className={`text-xs font-bold tracking-wider uppercase px-4 py-2 transition-colors ${mode === "funds" ? "bg-[#29324A] text-white" : "text-[#29324A] hover:bg-[#F8F4FB]"}`}
            >
              12-month funds
            </button>
            <button
              type="button"
              onClick={() => setMode("income")}
              data-testid="mode-income"
              className={`text-xs font-bold tracking-wider uppercase px-4 py-2 transition-colors ${mode === "income" ? "bg-[#29324A] text-white" : "text-[#29324A] hover:bg-[#F8F4FB]"}`}
            >
              Annual income
            </button>
          </div>
        </div>
      </section>

      {/* Calculator */}
      <section data-testid="funds-calculator-section" className="py-16 md:py-20 bg-[#F8F9FA] print:bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-12 gap-8 lg:gap-10 items-start">
            {/* Inputs */}
            <div className="lg:col-span-7 bg-white p-7 md:p-10 border border-slate-200">
              <div className="flex items-center gap-3 mb-8">
                <Calculator className="h-6 w-6 text-[#C43A89]" />
                <h2 className="font-display text-2xl md:text-3xl text-[#29324A] leading-tight">
                  {mode === "funds" ? "Tell us about your application" : "Annual income option"}
                </h2>
              </div>

              {mode === "funds" ? (
                <>
                  {/* Step 1: family */}
                  <fieldset className="mb-8">
                    <StepLegend icon={Users} n="1">Who will be on the application?</StepLegend>
                    <div className="space-y-4">
                      <label className="flex items-center gap-3 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={partner}
                          onChange={(e) => setPartner(e.target.checked)}
                          data-testid="calc-partner"
                          className="h-4 w-4 accent-[#29324A]"
                        />
                        <span className="text-sm text-slate-700">
                          Include partner / spouse <span className="text-slate-400">(+{fmtAUD(LIVING_PARTNER)} / 12 months)</span>
                        </span>
                      </label>
                      <div className="grid sm:grid-cols-2 gap-4">
                        <NumberInput
                          id="children"
                          label="Total dependent children"
                          value={children}
                          onChange={setChildren}
                          prefix="#"
                          step={1}
                          testid="calc-children"
                          tooltip={`Each dependent child adds ${fmtAUD(LIVING_CHILD)} of living costs for 12 months.`}
                        />
                        <NumberInput
                          id="schoolChildren"
                          label="School-aged children"
                          value={schoolChildren}
                          onChange={setSchoolChildren}
                          prefix="#"
                          step={1}
                          testid="calc-school-children"
                          tooltip={`Each school-aged child adds ${fmtAUD(SCHOOLING_PER_CHILD)} for annual school costs.`}
                        />
                      </div>
                    </div>
                  </fieldset>

                  {/* Step 2: course duration & tuition */}
                  <fieldset className="mb-8">
                    <StepLegend icon={BookOpen} n="2">Course details</StepLegend>

                    <label htmlFor="courseMonths" className="block mb-5">
                      <span className="text-[11px] font-bold tracking-[0.18em] text-[#29324A] uppercase mb-2 flex items-center gap-1.5">
                        Course duration <Tooltip>If your course is shorter than 12 months, living and schooling costs are pro-rated by months / 12.</Tooltip>
                      </span>
                      <div className="flex items-center gap-4">
                        <input
                          id="courseMonths"
                          type="range"
                          min={1}
                          max={12}
                          step={1}
                          value={courseMonths}
                          onChange={(e) => setCourseMonths(Number(e.target.value))}
                          data-testid="calc-months"
                          className="flex-1 accent-[#C43A89]"
                        />
                        <span className="font-display text-lg text-[#29324A] w-24 text-right">
                          {courseMonths} {courseMonths === 1 ? "month" : "months"}
                        </span>
                      </div>
                      {courseMonths < 12 && (
                        <p className="mt-2 text-[12px] text-[#C43A89] flex items-center gap-1.5">
                          <Clock className="h-3 w-3" /> Living &amp; schooling costs pro-rated to {Math.round((courseMonths / 12) * 100)}%.
                        </p>
                      )}
                    </label>

                    <div className="grid sm:grid-cols-2 gap-4">
                      <NumberInput
                        id="tuition"
                        label="Course tuition (full period)"
                        value={tuition}
                        onChange={setTuition}
                        testid="calc-tuition"
                        tooltip="Total tuition fee for the entire study period from your Confirmation of Enrolment (CoE)."
                      />
                      <NumberInput
                        id="prepaid"
                        label="Pre-paid tuition (deduct)"
                        value={tuitionPrepaid}
                        onChange={setTuitionPrepaid}
                        testid="calc-prepaid"
                        tooltip="Any tuition already paid evidenced by official receipts."
                      />
                    </div>
                  </fieldset>

                  {/* Step 3: OSHC + Travel */}
                  <fieldset className="mb-2">
                    <StepLegend icon={Plane} n="3">OSHC &amp; travel</StepLegend>
                    <div className="grid sm:grid-cols-2 gap-4">
                      <NumberInput
                        id="oshc"
                        label="OSHC estimate"
                        value={oshc}
                        onChange={setOshc}
                        testid="calc-oshc"
                        tooltip={`Indicative annual OSHC: single ~${fmtAUD(OSHC_SINGLE_DEFAULT)}, couple ~${fmtAUD(OSHC_COUPLE_DEFAULT)}, family ~${fmtAUD(OSHC_FAMILY_DEFAULT)}. Always quote your provider.`}
                      />
                      <div>
                        <span className="text-[11px] font-bold tracking-[0.18em] text-[#29324A] uppercase mb-2 flex items-center gap-1.5">
                          Application location
                          <Tooltip>Offshore applicants include indicative travel costs; onshore typically do not.</Tooltip>
                        </span>
                        <div className="flex flex-col gap-2">
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="radio"
                              name="loc"
                              checked={offshore}
                              onChange={() => setOffshore(true)}
                              data-testid="calc-offshore"
                              className="accent-[#29324A]"
                            />
                            <span className="text-sm text-slate-700">Applying outside Australia ({fmtAUD(TRAVEL_OFFSHORE)} travel)</span>
                          </label>
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="radio"
                              name="loc"
                              checked={!offshore}
                              onChange={() => setOffshore(false)}
                              data-testid="calc-onshore"
                              className="accent-[#29324A]"
                            />
                            <span className="text-sm text-slate-700">Applying from inside Australia</span>
                          </label>
                        </div>
                      </div>
                    </div>
                  </fieldset>
                </>
              ) : (
                /* Annual income alt */
                <div className="space-y-6">
                  <p className="text-sm text-slate-700 leading-relaxed">
                    As an alternative to demonstrating savings, you may evidence financial capacity through the <strong>personal annual income</strong> of the applicant or their spouse for the 12 months immediately before the application date.
                  </p>

                  <div className="space-y-4">
                    <label className="flex items-center gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={partner}
                        onChange={(e) => setPartner(e.target.checked)}
                        className="h-4 w-4 accent-[#29324A]"
                        data-testid="income-partner"
                      />
                      <span className="text-sm text-slate-700">I have a partner / spouse</span>
                    </label>
                    <NumberInput
                      id="children-i"
                      label="Total dependent children"
                      value={children}
                      onChange={setChildren}
                      prefix="#"
                      step={1}
                      testid="income-children"
                    />
                    <NumberInput
                      id="annualIncome"
                      label="Annual income (AUD)"
                      value={annualIncome}
                      onChange={setAnnualIncome}
                      step={1000}
                      testid="income-annual"
                      tooltip="The personal annual income of the applicant OR their spouse for the 12 months immediately before the visa application."
                    />
                  </div>

                  <div className={`p-5 border-l-4 ${incomeThreshold.meets ? "bg-emerald-50 border-emerald-500" : "bg-rose-50 border-rose-500"}`}>
                    <div className="text-[11px] font-bold tracking-[0.22em] uppercase mb-1.5">
                      {incomeThreshold.meets ? "✓ Meets threshold" : "✗ Below threshold"}
                    </div>
                    <p className="text-sm text-slate-700 leading-relaxed">
                      {incomeThreshold.hasFamily
                        ? <>Applicants with at least one family member must show personal annual income of at least <strong>{fmtAUD(INCOME_WITH_FAMILY)}</strong>.</>
                        : <>Single applicants (no family members) must show personal annual income of at least <strong>{fmtAUD(INCOME_SINGLE)}</strong>.</>
                      }
                    </p>
                  </div>
                </div>
              )}
            </div>

            {/* Summary */}
            <div className="lg:col-span-5 lg:sticky lg:top-32 self-start space-y-5">
              {mode === "funds" ? (
                <div className="bg-[#29324A] grain text-white p-7 md:p-9 relative overflow-hidden">
                  <span aria-hidden className="absolute top-0 left-0 h-1 w-20 bg-[#C43A89]" />
                  <div className="text-[10px] font-bold tracking-[0.22em] text-[#C43A89] uppercase mb-2">
                    Estimated total funds required
                  </div>
                  <div data-testid="calc-total" className="font-display text-4xl md:text-5xl tracking-tight">
                    {fmtAUD(breakdown.total)}
                  </div>
                  <p className="text-xs text-slate-400 mt-2">
                    Indicative {breakdown.months}-month funds estimate (AUD).
                  </p>

                  <div className="mt-7 space-y-2 text-sm">
                    <Row label={`Living — primary applicant (${breakdown.months}mo)`} value={breakdown.livingStudent} />
                    {partner && <Row label={`Living — partner (${breakdown.months}mo)`} value={breakdown.livingPartner} />}
                    {children > 0 && <Row label={`Living — ${children} ${children === 1 ? "child" : "children"}`} value={breakdown.livingChildren} />}
                    {schoolChildren > 0 && <Row label={`Schooling — ${schoolChildren} child${schoolChildren > 1 ? "ren" : ""}`} value={breakdown.schooling} />}
                    <Row label="Tuition (net of pre-paid)" value={breakdown.tuitionNet} />
                    <Row label="OSHC" value={breakdown.oshcCost} />
                    <Row label="Travel" value={breakdown.travel} dim={breakdown.travel === 0} />
                  </div>

                  <Link
                    to="/book-consultation"
                    data-testid="calc-book-cta"
                    className="mt-7 inline-flex items-center justify-center gap-2 w-full bg-[#C43A89] hover:bg-[#a22b6e] text-white text-xs font-bold tracking-wider uppercase py-3.5 transition-colors btn-shimmer"
                  >
                    Talk to a Registered Migration Agent <ArrowRight className="h-3.5 w-3.5" />
                  </Link>

                  <button
                    type="button"
                    onClick={handlePrint}
                    data-testid="calc-print"
                    className="mt-3 inline-flex items-center justify-center gap-2 w-full border border-white/30 hover:bg-white/10 text-white text-[11px] font-bold tracking-wider uppercase py-3 transition-colors"
                  >
                    <Printer className="h-3.5 w-3.5" /> Print / Save summary
                  </button>
                </div>
              ) : (
                <div className={`grain text-white p-7 md:p-9 relative overflow-hidden ${incomeThreshold.meets ? "bg-emerald-700" : "bg-[#29324A]"}`}>
                  <span aria-hidden className="absolute top-0 left-0 h-1 w-20 bg-[#C43A89]" />
                  <div className="text-[10px] font-bold tracking-[0.22em] text-[#C43A89] uppercase mb-2">
                    Annual income required
                  </div>
                  <div className="font-display text-4xl md:text-5xl tracking-tight">
                    {fmtAUD(incomeThreshold.threshold)}
                  </div>
                  <p className="text-xs text-slate-200 mt-2">
                    {incomeThreshold.hasFamily ? "With family member(s)" : "Single applicant (no family member)"}
                  </p>

                  <div className="mt-6 bg-white/10 p-4">
                    <div className="text-[10px] tracking-[0.22em] uppercase text-white/80">Your income</div>
                    <div className="font-display text-2xl mt-1">{fmtAUD(annualIncome)}</div>
                    <div className="text-[12px] mt-1 text-white/80">
                      {incomeThreshold.meets ? "Above the threshold." : `Short by ${fmtAUD(incomeThreshold.threshold - annualIncome)}.`}
                    </div>
                  </div>

                  <Link
                    to="/book-consultation"
                    data-testid="calc-book-cta-income"
                    className="mt-7 inline-flex items-center justify-center gap-2 w-full bg-[#C43A89] hover:bg-[#a22b6e] text-white text-xs font-bold tracking-wider uppercase py-3.5 transition-colors btn-shimmer"
                  >
                    Discuss my evidence with an agent <ArrowRight className="h-3.5 w-3.5" />
                  </Link>
                </div>
              )}

              {/* Quick info */}
              <div className="bg-white border border-slate-200 p-5 flex gap-3 print:hidden">
                <Info className="h-5 w-5 text-[#29324A] shrink-0 mt-0.5" />
                <p className="text-xs text-slate-500 leading-relaxed">
                  Calculations follow the post-<strong>10 May 2024</strong> figures published by the Department of Home Affairs. Final acceptance of any evidence is at the Department's discretion.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Notes — Genuine access / source of funds / sponsor evidence */}
      <section data-testid="funds-notes" className="py-16 md:py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mb-10">
            <div className="text-[10px] font-bold tracking-[0.22em] text-[#C43A89] uppercase mb-3">
              Important — How to evidence your funds
            </div>
            <h2 className="font-display text-2xl md:text-3xl text-[#29324A] tracking-tight leading-tight">
              The Department looks at <em>genuine</em>, accessible funds — not just the number on paper.
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-5">
            {[
              {
                icon: HeartHandshake,
                title: "Genuine access to funds",
                desc: "You must be able to genuinely access the funds while studying in Australia. Recently moved-in or borrowed lump sums without a clear source are routinely scrutinised.",
              },
              {
                icon: FileSearch,
                title: "Source of funds",
                desc: "Be ready to evidence where the money came from — payslips, business statements, sale proceeds, inheritance documents, tax records. Unexplained large deposits weaken your case.",
              },
              {
                icon: Wallet,
                title: "Financial sponsor evidence",
                desc: "If funds belong to a sponsor (parent, spouse, etc.), provide the sponsor's bank statements, employment / business income, plus a declaration of relationship and intention to support.",
              },
            ].map((n, i) => {
              const Icon = n.icon;
              return (
                <div key={i} className="bg-white border border-slate-200 hover:border-[#C43A89] transition-colors p-6 md:p-7 group">
                  <span aria-hidden className="block h-0.5 w-10 bg-[#C43A89] mb-5 group-hover:w-full transition-all duration-500" />
                  <Icon className="h-7 w-7 text-[#29324A] mb-3" />
                  <h3 className="font-display text-lg text-[#29324A] mb-2 leading-snug">{n.title}</h3>
                  <p className="text-[13px] text-slate-600 leading-relaxed">{n.desc}</p>
                </div>
              );
            })}
          </div>

          <div className="mt-10 bg-[#F8F4FB] border-l-4 border-[#29324A] p-6 flex items-start gap-3">
            <ShieldCheck className="h-5 w-5 text-[#29324A] mt-0.5 shrink-0" />
            <div className="text-[13px] text-slate-700 leading-relaxed">
              <strong className="text-[#29324A]">Disclaimer:</strong> This calculator and the notes above are general guidance only. They do <strong>not</strong> guarantee a Student Visa grant and do not replace personalised migration advice. Department of Home Affairs financial capacity figures are reviewed periodically — always confirm current values at{" "}
              <a href="https://immi.homeaffairs.gov.au" target="_blank" rel="noopener noreferrer" className="text-[#29324A] underline font-semibold">immi.homeaffairs.gov.au</a>{" "}
              or book a paid consultation with our Registered Migration Agents.
            </div>
          </div>
        </div>
      </section>

      {/* What's inside your living costs — interactive breakdown */}
      <CostBreakdownSection />

      {/* FAQ */}
      <FaqSection />

      <CTASection title="Need help preparing your Student Visa financial evidence?" />
    </div>
  );
}

// ─── Cost-of-living breakdown — interactive boxes ───────────────────────────
const COST_ITEMS = [
  { icon: Home,        label: "Accommodation",    weekly: "AUD 250 – 600",   note: "Share house / on-campus / private rental varies by city." },
  { icon: Coins,       label: "Groceries & food", weekly: "AUD 140 – 280",   note: "Cooking at home keeps weekly grocery spend low." },
  { icon: Bus,         label: "Public transport", weekly: "AUD 20 – 50",     note: "Most cities offer student concession (varies by state)." },
  { icon: Stethoscope, label: "OSHC (health)",    weekly: "AUD 14 – 100",    note: "Single ~AUD 750/yr; couple ~AUD 3,800/yr; family ~AUD 5,500/yr." },
  { icon: BookOpen,    label: "Course materials", weekly: "AUD 20 – 60",     note: "Books, software, lab/clinical kit, online subscriptions." },
  { icon: Wallet,      label: "Personal & misc",  weekly: "AUD 80 – 180",    note: "Phone, internet, entertainment, clothing, dining out." },
];

function CostBreakdownSection() {
  return (
    <section data-testid="cost-breakdown-section" className="py-16 md:py-20 bg-[#F8F9FA] border-t border-slate-200">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mb-10">
          <div className="text-[10px] font-bold tracking-[0.22em] text-[#C43A89] uppercase mb-3">
            What does your living budget actually cover?
          </div>
          <h2 className="font-display text-2xl md:text-3xl text-[#29324A] tracking-tight leading-tight">
            Indicative weekly living costs for students in Australia
          </h2>
          <p className="mt-3 text-sm text-slate-600 leading-relaxed">
            The Department of Home Affairs requires AUD 29,710 of <em>living costs</em> per primary applicant for 12 months — roughly <strong className="text-[#29324A]">AUD 570 per week</strong>. Here's how that typically breaks down across daily expenses.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
          {COST_ITEMS.map((item, i) => {
            const Icon = item.icon;
            return (
              <div
                key={i}
                data-testid={`cost-item-${i}`}
                className="group bg-white border border-slate-200 hover:border-[#C43A89] transition-all hover:-translate-y-1 hover:shadow-[0_18px_40px_-12px_rgba(196,58,137,0.18)] p-5 md:p-6 flex gap-4"
              >
                <div className="shrink-0 h-12 w-12 rounded-full bg-gradient-to-br from-[#29324A] to-[#C43A89] text-white flex items-center justify-center shadow-md">
                  <Icon className="h-5 w-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-baseline justify-between gap-3 mb-1">
                    <h3 className="font-display text-base text-[#29324A] leading-snug">{item.label}</h3>
                  </div>
                  <div className="text-[12px] font-bold tracking-wider text-[#29324A]">{item.weekly} / week</div>
                  <p className="mt-1.5 text-[12px] text-slate-600 leading-relaxed">{item.note}</p>
                </div>
              </div>
            );
          })}
        </div>

        <div className="mt-8 grid sm:grid-cols-3 gap-4">
          <div className="bg-white border-l-4 border-[#29324A] p-5">
            <div className="text-[10px] font-bold tracking-[0.22em] text-[#29324A] uppercase mb-1">Sydney / Melbourne</div>
            <div className="font-display text-lg text-[#29324A]">Higher end of range</div>
            <p className="text-[12px] text-slate-600 mt-1 leading-relaxed">Capital-city rent + transport push living costs up; budget ~AUD 600+ per week.</p>
          </div>
          <div className="bg-white border-l-4 border-[#C43A89] p-5">
            <div className="text-[10px] font-bold tracking-[0.22em] text-[#C43A89] uppercase mb-1">Brisbane / Perth / Adelaide</div>
            <div className="font-display text-lg text-[#29324A]">Mid range</div>
            <p className="text-[12px] text-slate-600 mt-1 leading-relaxed">More affordable rental markets; students typically manage on AUD 500–550 per week.</p>
          </div>
          <div className="bg-white border-l-4 border-[#29324A] p-5">
            <div className="text-[10px] font-bold tracking-[0.22em] text-[#29324A] uppercase mb-1">Regional Australia</div>
            <div className="font-display text-lg text-[#29324A]">Lower end of range</div>
            <p className="text-[12px] text-slate-600 mt-1 leading-relaxed">Significantly cheaper rentals — often AUD 450–500 per week is achievable.</p>
          </div>
        </div>
      </div>
    </section>
  );
}

// ─── FAQ ────────────────────────────────────────────────────────────────────
const FAQS = [
  {
    q: "What is the minimum financial capacity required for a Student Visa (Subclass 500)?",
    a: "From 10 May 2024, the Department of Home Affairs requires AUD 29,710 in living costs per primary applicant for 12 months, plus tuition, OSHC and (if offshore) indicative travel. Family members add AUD 10,394 for a partner and AUD 4,449 per child. School-aged children also need AUD 13,502 per year for schooling.",
  },
  {
    q: "Can I use the annual income option instead of showing savings?",
    a: "Yes. As an alternative, you (or your spouse) can evidence personal annual income of at least AUD 87,856 for a single applicant or AUD 102,500 where a family member is included. The income must be for the 12 months immediately before the application date.",
  },
  {
    q: "Whose funds can I use to demonstrate financial capacity?",
    a: "Funds can belong to the applicant, the applicant's spouse/partner, or a financial sponsor (parents, siblings, other relatives). Sponsor funds require additional evidence — sponsor bank statements, payslips/business records, and a declaration of relationship and intention to support.",
  },
  {
    q: "What types of evidence does the Department accept?",
    a: "Acceptable evidence typically includes bank statements (last 3–6 months), term deposits, fixed deposits with a recognised financial institution, education loan letters (from approved lenders), and government-recognised scholarships. Cash declarations are generally not acceptable.",
  },
  {
    q: "Is OSHC included in the financial capacity requirement?",
    a: "OSHC is a separate Student Visa requirement — you must have valid cover for the duration of your visa. While not strictly part of the AUD 29,710 living costs amount, you must show you can afford OSHC premiums (single ~AUD 750/yr, couple ~AUD 3,800/yr, family ~AUD 5,500/yr as indicative figures).",
  },
  {
    q: "What if my course is less than 12 months?",
    a: "Living costs and schooling costs are pro-rated to the course length. For example, a 6-month course requires roughly half of the 12-month living costs amount. Tuition is calculated based on your actual Confirmation of Enrolment (CoE).",
  },
  {
    q: "Will the Department actually verify that I have access to these funds?",
    a: "Yes. Case officers regularly scrutinise the source of funds, especially recent large deposits. You should be able to clearly explain where the money came from — savings, salary, business income, sale of property, inheritance, or scholarship — and provide supporting documents.",
  },
  {
    q: "Does meeting the financial threshold guarantee a visa grant?",
    a: "No. Financial capacity is one of several criteria. You must also satisfy the Genuine Student requirement, English language requirement, OSHC, character and health checks, and any other Public Interest Criteria. Final decisions are at the discretion of the Department of Home Affairs.",
  },
];

function FaqItem({ item, isOpen, onToggle, idx }) {
  return (
    <div data-testid={`faq-item-${idx}`} className="bg-white border border-slate-200 overflow-hidden">
      <button
        type="button"
        onClick={onToggle}
        data-testid={`faq-toggle-${idx}`}
        aria-expanded={isOpen}
        className="w-full flex items-center justify-between gap-4 text-left px-5 md:px-6 py-4 hover:bg-[#F8F4FB] transition-colors"
      >
        <span className="font-display text-[15px] md:text-base text-[#29324A] leading-snug">
          {item.q}
        </span>
        <ChevronDown
          className={`h-5 w-5 text-[#C43A89] shrink-0 transition-transform duration-300 ${isOpen ? "rotate-180" : ""}`}
        />
      </button>
      <div
        className={`grid transition-[grid-template-rows] duration-300 ease-in-out ${isOpen ? "grid-rows-[1fr]" : "grid-rows-[0fr]"}`}
      >
        <div className="overflow-hidden">
          <div className="px-5 md:px-6 pb-5 pt-1 border-t border-slate-100 text-[13px] text-slate-700 leading-relaxed">
            {item.a}
          </div>
        </div>
      </div>
    </div>
  );
}

function FaqSection() {
  const [open, setOpen] = useState(0);

  return (
    <section data-testid="faq-section" className="py-16 md:py-20 bg-white border-t border-slate-200">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-10 text-center">
          <div className="text-[10px] font-bold tracking-[0.22em] text-[#C43A89] uppercase mb-3 flex items-center justify-center gap-2">
            <AlertCircle className="h-3.5 w-3.5" /> Frequently asked questions
          </div>
          <h2 className="font-display text-3xl md:text-4xl text-[#29324A] tracking-tight leading-tight">
            Student Visa funds — your questions, answered
          </h2>
        </div>

        <div className="space-y-3">
          {FAQS.map((item, i) => (
            <FaqItem
              key={i}
              idx={i}
              item={item}
              isOpen={open === i}
              onToggle={() => setOpen(open === i ? -1 : i)}
            />
          ))}
        </div>

        <div className="mt-10 bg-[#29324A] text-white p-6 md:p-8 grain relative flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <div className="text-[10px] font-bold tracking-[0.22em] text-[#C43A89] uppercase mb-2">Still have questions?</div>
            <h3 className="font-display text-xl md:text-2xl leading-snug">
              Book a 30-minute consultation with a Registered Migration Agent.
            </h3>
          </div>
          <Link
            to="/book-consultation"
            data-testid="faq-book-cta"
            className="shrink-0 inline-flex items-center justify-center gap-2 bg-[#C43A89] hover:bg-[#a22b6e] text-white text-xs font-bold tracking-wider uppercase px-6 py-3.5 transition-colors btn-shimmer"
          >
            Book consultation <ArrowRight className="h-3.5 w-3.5" />
          </Link>
        </div>
      </div>
    </section>
  );
}

